import { useState } from "react";
import { Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useEditDealField } from "./use-edit-deal-field";
import { useBulkEditMaybe } from "./bulk-edit-context";

interface CurrentRound {
  amount: string;
  currency: string;
}

interface PreviousRound {
  round_type: string;
  amount: string;
  currency: string;
  investors: string;
  round_date: string;
  valuation: string;
}

interface Props {
  currentRound: CurrentRound;
  previousRounds: PreviousRound[];
  /** Read-mode rendering of the section body (caller renders rows). */
  readChildren: React.ReactNode;
}

const isNA = (v: unknown) => !v || v === "N/A";
const emptyPrev = (): PreviousRound => ({
  round_type: "",
  amount: "",
  currency: "",
  investors: "",
  round_date: "",
  valuation: "",
});

/**
 * Fundraising editor. Shows the read-only rows rendered by the caller, plus
 * a pencil button in the top-right that opens a dialog to edit current round
 * amount/currency and add/remove previous rounds. Persists both
 * `current_funding_round` and `previous_funding_rounds` fields.
 */
export function EditableFundraisingField({
  currentRound,
  previousRounds,
  readChildren,
}: Props) {
  const { save } = useEditDealField();
  const bulk = useBulkEditMaybe();
  const inBulk = bulk?.enabled ?? false;
  const [open, setOpen] = useState(false);
  const [draftCurrent, setDraftCurrent] = useState<CurrentRound>({
    amount: "",
    currency: "",
  });
  const [draftPrev, setDraftPrev] = useState<PreviousRound[]>([]);
  const [saving, setSaving] = useState(false);

  const openDialog = () => {
    setDraftCurrent({
      amount: isNA(currentRound.amount) ? "" : currentRound.amount,
      currency: isNA(currentRound.currency) ? "" : currentRound.currency,
    });
    setDraftPrev(
      previousRounds.map((r) => ({
        round_type: isNA(r.round_type) ? "" : r.round_type,
        amount: isNA(r.amount) ? "" : r.amount,
        currency: isNA(r.currency) ? "" : r.currency,
        investors: isNA(r.investors) ? "" : r.investors,
        round_date: isNA(r.round_date) ? "" : r.round_date,
        valuation: isNA(r.valuation) ? "" : r.valuation,
      }))
    );
    setOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await save("current_funding_round", draftCurrent as unknown as Record<string, unknown>);
      await save("previous_funding_rounds", {
        previous_funding: draftPrev,
        total_rounds: draftPrev.length,
      } as unknown as Record<string, unknown>);
      setOpen(false);
    } finally {
      setSaving(false);
    }
  };

  const updatePrev = (idx: number, key: keyof PreviousRound, value: string) => {
    setDraftPrev((prev) =>
      prev.map((r, i) => (i === idx ? { ...r, [key]: value } : r))
    );
  };

  return (
    <>
      <div className="relative">
        {inBulk && (
          <button
            type="button"
            onClick={openDialog}
            className="absolute right-3 top-2 flex h-6 w-6 items-center justify-center rounded text-muted-foreground/60 transition-colors hover:bg-foreground/[0.06] hover:text-foreground"
            title="Edit Fundraising"
          >
            <Pencil className="h-3 w-3" />
          </button>
        )}
        {readChildren}
      </div>

      <Dialog open={open} onOpenChange={(v) => !saving && setOpen(v)}>
        <DialogContent className="sm:max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit fundraising</DialogTitle>
          </DialogHeader>

          <div className="space-y-5 py-2">
            {/* Current round */}
            <div>
              <h3 className="mb-2 text-xs font-medium uppercase tracking-widest text-muted-foreground/60">
                Current round
              </h3>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="cr-amount" className="text-xs">
                    Amount
                  </Label>
                  <Input
                    id="cr-amount"
                    value={draftCurrent.amount}
                    onChange={(e) =>
                      setDraftCurrent({
                        ...draftCurrent,
                        amount: e.target.value,
                      })
                    }
                    placeholder="e.g. 2,000,000"
                  />
                </div>
                <div>
                  <Label htmlFor="cr-currency" className="text-xs">
                    Currency
                  </Label>
                  <Input
                    id="cr-currency"
                    value={draftCurrent.currency}
                    onChange={(e) =>
                      setDraftCurrent({
                        ...draftCurrent,
                        currency: e.target.value,
                      })
                    }
                    placeholder="USD"
                  />
                </div>
              </div>
            </div>

            {/* Previous rounds */}
            <div>
              <div className="mb-2 flex items-center justify-between">
                <h3 className="text-xs font-medium uppercase tracking-widest text-muted-foreground/60">
                  Previous rounds
                </h3>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setDraftPrev((p) => [...p, emptyPrev()])}
                  className="h-7 gap-1.5"
                >
                  <Plus className="h-3 w-3" />
                  Add round
                </Button>
              </div>

              {draftPrev.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                  No previous rounds.
                </p>
              ) : (
                <div className="space-y-3">
                  {draftPrev.map((r, idx) => (
                    <div
                      key={idx}
                      className="rounded-md border border-border p-3"
                    >
                      <div className="mb-2 flex items-center justify-between">
                        <span className="text-xs font-medium text-muted-foreground">
                          Round {idx + 1}
                        </span>
                        <button
                          type="button"
                          onClick={() =>
                            setDraftPrev((p) => p.filter((_, i) => i !== idx))
                          }
                          className="flex h-6 w-6 items-center justify-center rounded text-destructive/70 hover:bg-destructive/10 hover:text-destructive"
                          title="Remove round"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-xs">Round type</Label>
                          <Input
                            value={r.round_type}
                            onChange={(e) =>
                              updatePrev(idx, "round_type", e.target.value)
                            }
                            placeholder="e.g. Seed"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Date</Label>
                          <Input
                            value={r.round_date}
                            onChange={(e) =>
                              updatePrev(idx, "round_date", e.target.value)
                            }
                            placeholder="YYYY-MM-DD"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Amount</Label>
                          <Input
                            value={r.amount}
                            onChange={(e) =>
                              updatePrev(idx, "amount", e.target.value)
                            }
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Currency</Label>
                          <Input
                            value={r.currency}
                            onChange={(e) =>
                              updatePrev(idx, "currency", e.target.value)
                            }
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Valuation</Label>
                          <Input
                            value={r.valuation}
                            onChange={(e) =>
                              updatePrev(idx, "valuation", e.target.value)
                            }
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Investors</Label>
                          <Input
                            value={r.investors}
                            onChange={(e) =>
                              updatePrev(idx, "investors", e.target.value)
                            }
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving…
                </>
              ) : inBulk ? (
                "Done"
              ) : (
                "Save"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
